import { useState, type FC } from "react";
import { Quote } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import type { VoiceColumn } from "../Types/ListeningReportTypes";
import { CardTitle, REPORT_KEY, ReportBadge, ReportTag, SectionTitle, ViewPostLink } from "./ReportPrimitives";

const VOICES_KEY = `${REPORT_KEY}.voices`;
const COLLAPSED_QUOTE_COUNT = 3;

const VoiceCard: FC<{ column: VoiceColumn }> = ({ column }) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const visibleQuotes = expanded ? column.quotes : column.quotes.slice(0, COLLAPSED_QUOTE_COUNT);
  const canExpand = column.quotes.length > COLLAPSED_QUOTE_COUNT;

  return (
    <div className="br__card br__card--flush br__voice br__c4">
      <div className="br__card-top">
        <div>
          <CardTitle help={t(`${VOICES_KEY}.help`, "Representative comments, lightly trimmed")}>
            {column.title}
          </CardTitle>
          <p className="br__card-desc">{column.countLabel}</p>
        </div>
        <ReportBadge tone={column.badge.tone}>{column.badge.label}</ReportBadge>
      </div>
      <div className="br__qlist">
        {visibleQuotes.map(quote => (
          <div className="br__qrow" key={quote.text}>
            <span className="br__sq">
              <Quote className="br-ic" aria-hidden />
            </span>
            <div>
              <p>{quote.text}</p>
              <div className="br__qtags">
                <ReportTag small tone={column.sentiment.tone}>
                  {column.sentiment.label}
                </ReportTag>
                <ReportTag small>{quote.topic}</ReportTag>
              </div>
            </div>
            <ViewPostLink small url={quote.url} />
          </div>
        ))}
      </div>
      <div className="br__qfoot">
        <span>
          {t(`${VOICES_KEY}.showing`, "Showing {{shown}} of {{total}} comments", {
            shown: formatNumber(visibleQuotes.length, {}, i18n.language),
            total: formatNumber(column.total, {}, i18n.language),
          })}
        </span>
        {canExpand && (
          <button
            type="button"
            className="br__see-more"
            aria-expanded={expanded}
            onClick={() => setExpanded(current => !current)}
          >
            {expanded
              ? t(`${VOICES_KEY}.showFewer`, "Show fewer")
              : t(`${VOICES_KEY}.seeMore`, "See more comments")}
          </button>
        )}
      </div>
    </div>
  );
};

interface VoicesSectionProps {
  columns: VoiceColumn[];
  primaryName: string;
}

const VoicesSection: FC<VoicesSectionProps> = ({ columns, primaryName }) => {
  const { t } = useTranslation();

  return (
    <section className="br__panel" id="br-voices" data-pdf-block="br-voices" data-pdf-splittable aria-label={t(`${REPORT_KEY}.tabs.voices`, "Customer Voice")}>
      <SectionTitle
        scope={{ label: t(`${REPORT_KEY}.scope.only`, "{{primary}} only", { primary: primaryName }) }}
      >
        {t(`${REPORT_KEY}.tabs.voices`, "Customer Voice")}
      </SectionTitle>
      <div className="br__grid">
        {columns.map(column => (
          <VoiceCard key={column.id} column={column} />
        ))}
      </div>
    </section>
  );
};

export default VoicesSection;
